import React from "react";
import { motion } from "framer-motion";

function LegalSection({ number, title, children }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      className="group relative w-full overflow-hidden rounded-3xl border border-white/10 bg-white/[0.035] p-6 transition duration-300 hover:border-white/[0.16] hover:bg-white/[0.05] sm:p-8 lg:p-10"
    >
      <div className="pointer-events-none absolute right-0 top-0 h-56 w-56 rounded-full bg-pink-500/[0.025] blur-3xl" />

      <div className="relative flex items-start gap-4 sm:gap-5 lg:gap-6">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.05] text-sm font-black text-pink-300 sm:h-14 sm:w-14 sm:text-base">
          {number}
        </div>

        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-bold text-white sm:text-2xl lg:text-[26px]">
            {title}
          </h2>

          <div className="mt-5 space-y-4 text-sm leading-7 text-slate-300 sm:text-base sm:leading-8 lg:text-[17px] [&_strong]:font-semibold [&_strong]:text-white">
            {children}
          </div>
        </div>
      </div>
    </motion.article>
  );
}

function InfoBox({ title, children }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/10 p-5">
      <span className="block text-xs uppercase tracking-wider text-slate-500">
        {title}
      </span>

      <strong className="mt-2 block break-words text-white">
        {children}
      </strong>
    </div>
  );
}

export default function PrivacyPolicy() {
  return (
    <main className="w-full overflow-hidden">

      <section className="relative w-full lg:w-[80%] mx-auto overflow-hidden px-4 py-20 sm:px-6">

        <div className="pointer-events-none absolute left-1/2 top-20 h-[300px] w-[700px] -translate-x-1/2 rounded-full bg-violet-600/10 blur-[140px]" />

        <div className="relative mx-auto mb-14 w-full max-w-6xl text-center">

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-pink-300"
          >
            Privacy & Data Protection
          </motion.div>


          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="mt-5 text-4xl font-black text-white sm:text-5xl lg:text-6xl"
          >
            OjMee Technology

            <span className="mt-2 block bg-gradient-to-r from-pink-400 via-fuchsia-400 to-violet-400 bg-clip-text text-transparent">
              Privacy Policy
            </span>
          </motion.h1>


          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mx-auto mt-6 max-w-5xl text-base leading-8 text-slate-300 sm:text-lg"
          >
            OjMee Technology respects the privacy of individuals,
            businesses and organizations that use our websites,
            SaaS platforms, business software, applications and
            digital services. This Privacy Policy explains how
            information may be collected, used, stored, processed,
            shared and protected when you interact with OjMee Technology
            or use our software products and services.
          </motion.p>


          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.25 }}
            className="mt-7 flex flex-wrap justify-center gap-3"
          >
            <span className="rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-xs text-slate-400 sm:text-sm">
              Effective Date: 24 September 2026
            </span>

            <span className="rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-xs text-slate-400 sm:text-sm">
              Last Updated: 24 September 2026
            </span>
          </motion.div>

        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative mb-8 w-full rounded-3xl border border-white/10 bg-white/[0.035] p-6 backdrop-blur-sm sm:p-8 lg:p-10"
        >
          <h2 className="text-xl font-bold text-white sm:text-2xl">
            Your Privacy Matters to Us
          </h2>

          <p className="mt-4 text-sm leading-8 text-slate-300 sm:text-base lg:text-[17px]">
            This Privacy Policy applies to OjMee Technology websites,
            SaaS products, cloud platforms, business applications,
            mobile applications and other digital services that link
            to or reference this Policy.
          </p>

          <p className="mt-4 text-sm leading-8 text-slate-300 sm:text-base lg:text-[17px]">
            This includes applicable OjMee software products such as
            invoicing, billing, HRMS, CRM, ERP, analytics, remote-access
            and other business software made available by OjMee Technology.
          </p>

          <p className="mt-4 text-sm leading-8 text-slate-300 sm:text-base lg:text-[17px]">
            By accessing or using our services, you acknowledge the
            information-handling practices described in this Privacy Policy.
          </p>
        </motion.div>

        <div className="relative w-full space-y-5">

          <LegalSection
            number="01"
            title="Scope of this Privacy Policy"
          >
            <p>
              This Privacy Policy applies to OjMee Technology websites,
              SaaS platforms, cloud-based business software, mobile
              applications and other digital services that display or
              reference this Policy.
            </p>

            <p>
              It describes how information may be handled when you visit
              our websites, create an account, subscribe to a software
              product, use our applications, make a payment, request
              support, contact our team or otherwise interact with
              OjMee Technology.
            </p>

            <p>
              Certain OjMee products may provide additional privacy
              information where a particular product processes information
              differently or requires product-specific disclosures.
            </p>
          </LegalSection>

          <LegalSection
            number="02"
            title="Information We May Collect"
          >
            <p>
              Depending on the product or service you use, we may collect
              account and contact information such as your name, business
              name, email address, mobile number, address and
              authentication-related information.
            </p>

            <p>
              Business software may allow users to provide organization
              information, GSTIN, billing details, customer information,
              employee information, product or service records, invoices,
              quotations, transaction information, operational records
              and other information required to use the relevant software.
            </p>

            <p>
              Subscription and payment-related information may include
              plan details, payment amounts, transaction references,
              transaction IDs, UTR references, payment status and other
              information required to identify or verify a transaction.
            </p>

            <p>
              We may also process technical and usage information such as
              IP address, browser type, operating system, device
              information, timestamps, application activity, diagnostic
              information, security logs and other technical information
              necessary to operate and protect our services.
            </p>
          </LegalSection>

          <LegalSection
            number="03"
            title="Business and Customer Data"
          >
            <p>
              Some OjMee Technology SaaS products allow businesses to
              enter, upload, store and manage information relating to
              their customers, employees, suppliers, products,
              transactions or other business activities.
            </p>

            <p>
              The business or organization using the service is
              responsible for determining whether it has the appropriate
              authority or lawful basis to collect, use and provide such
              information through the relevant OjMee product.
            </p>

            <p>
              OjMee Technology processes such information as necessary
              to provide, maintain and support the requested software
              functionality, subject to applicable agreements,
              instructions and legal requirements.
            </p>
          </LegalSection>

          <LegalSection
            number="04"
            title="How We Use Information"
          >
            <p>
              We may use information to create and authenticate accounts,
              provide SaaS functionality, operate business software,
              process user requests, maintain records and deliver the
              features available through our products.
            </p>

            <p>
              Depending on the software being used, this may include
              generating invoices, maintaining customer records,
              managing employees, organizing sales information,
              processing workflows, generating reports, providing
              analytics or enabling other business-management functions.
            </p>

            <p>
              Information may also be used to verify payments, manage
              subscriptions, provide customer support, troubleshoot
              technical issues, maintain system reliability, detect or
              prevent misuse, protect our services and improve our
              software products.
            </p>

            <p>
              We may also process information where reasonably necessary
              to comply with applicable legal, regulatory, accounting,
              security or contractual requirements.
            </p>

            <p>
              Where consent is applicable to a particular processing
              activity, users may have the ability to withdraw that
              consent subject to applicable law and any processing that
              remains necessary for another lawful purpose.
            </p>
          </LegalSection>

          <motion.article
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative w-full overflow-hidden rounded-3xl border border-pink-400/20 bg-gradient-to-br from-pink-500/[0.08] via-fuchsia-500/[0.04] to-violet-500/[0.08] p-6 sm:p-8 lg:p-10"
          >
            <div className="pointer-events-none absolute -right-16 -top-16 h-52 w-52 rounded-full bg-pink-500/10 blur-3xl" />

            <div className="relative">

              <div className="flex items-start gap-4 sm:gap-5 lg:gap-6">

                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-pink-400/20 bg-pink-500/10 font-black text-pink-300 sm:h-14 sm:w-14">
                  05
                </div>

                <div>
                  <span className="inline-flex rounded-full border border-pink-400/20 bg-pink-500/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-pink-300">
                    Payment Security
                  </span>

                  <h2 className="mt-3 text-xl font-bold text-white sm:text-2xl lg:text-[26px]">
                    Payment Information
                  </h2>
                </div>

              </div>


              <div className="mt-6 space-y-4 text-sm leading-8 text-slate-300 sm:text-base lg:text-[17px]">

                <p>
                  OjMee Technology may receive transaction information
                  required to identify, verify and manage subscription,
                  product or service payments. This may include transaction
                  references, UTR or transaction IDs, payment amounts,
                  payment status and related metadata.
                </p>

                <p>
                  Payments may be processed through banks, UPI applications,
                  payment gateways or other independent payment service
                  providers. Those providers may independently collect and
                  process payment information according to their own terms,
                  security practices and privacy policies.
                </p>


                <div className="rounded-2xl border border-white/10 bg-white/[0.05] p-5 sm:p-6">

                  <p className="font-semibold text-white">

                    OjMee Technology does not require you to provide your{" "}

                    <span className="text-pink-300">
                      UPI PIN, OTP, debit or credit card PIN, or banking
                      password
                    </span>{" "}

                    through our website, application, email, chat or
                    customer-support channels.

                  </p>

                </div>


                <p>
                  Never share your UPI PIN, OTP, banking password or other
                  confidential authentication credentials with anyone
                  claiming to represent OjMee Technology.
                </p>

              </div>

            </div>

          </motion.article>

          <LegalSection
            number="06"
            title="Data Sharing and Service Providers"
          >
            <p>
              OjMee Technology does not sell personal information as a
              business practice.
            </p>

            <p>
              Information may be shared with third-party service providers
              where reasonably necessary to operate, maintain or support
              our products and services.
            </p>

            <p>
              These providers may include cloud hosting and infrastructure
              providers, database services, email or communication
              providers, analytics providers, payment processors,
              security services and other technology partners required
              for service operation.
            </p>

            <p>
              Information may also be disclosed where required by
              applicable law, lawful governmental request, legal process
              or court order, or where reasonably necessary to protect
              users, our rights, our systems or the security and integrity
              of our services.
            </p>
          </LegalSection>

          <LegalSection
            number="07"
            title="Data Security"
          >
            <p>
              We use reasonable technical and organizational measures
              intended to protect information against unauthorized
              access, alteration, disclosure, misuse, loss or destruction.
            </p>

            <p>
              Depending on the product, security measures may include
              account authentication, authorization controls, protected
              infrastructure, secure communication mechanisms, access
              restrictions, monitoring and other safeguards appropriate
              to the nature of the service.
            </p>

            <p>
              We continually evaluate our systems and may update security
              practices as our technology, products and infrastructure
              evolve.
            </p>

            <p>
              However, no internet-based software, cloud platform,
              transmission method or storage system can guarantee
              absolute security.
            </p>

            <p>
              Users are responsible for maintaining secure passwords,
              protecting devices and credentials and preventing
              unauthorized access to their accounts.
            </p>
          </LegalSection>

          <LegalSection
            number="08"
            title="Cloud Infrastructure and Data Processing"
          >
            <p>
              OjMee Technology provides cloud-based and SaaS software.
              Information used through these services may therefore be
              processed or stored using cloud infrastructure, databases
              and technology service providers used to operate the
              relevant product.
            </p>

            <p>
              We may use infrastructure and service providers located in
              different geographic locations depending on operational,
              availability, performance and service requirements.
            </p>

            <p>
              Where applicable, we take reasonable steps intended to
              ensure that service providers handling information are
              used consistently with applicable contractual and legal
              requirements.
            </p>
          </LegalSection>

          <LegalSection
            number="09"
            title="Data Retention"
          >
            <p>
              Information may be retained for as long as reasonably
              necessary to provide our products and services, maintain
              accounts, support business operations and satisfy the
              purposes described in this Privacy Policy.
            </p>

            <p>
              Information may also be retained where reasonably necessary
              for accounting, taxation, security, fraud prevention,
              dispute resolution, enforcement of agreements or applicable
              legal and regulatory requirements.
            </p>

            <p>
              Retention periods may vary depending on the type of
              information, the software product being used, the purpose
              of processing and applicable requirements.
            </p>

            <p>
              Certain records may therefore continue to be retained after
              an account, subscription or business relationship has ended
              where retention is reasonably necessary or legally required.
            </p>
          </LegalSection>

          <LegalSection
            number="10"
            title="User Choices and Privacy Rights"
          >
            <p>
              Subject to applicable law, users may have rights relating
              to access, correction, updating, deletion or other handling
              of their personal information.
            </p>

            <p>
              Users may contact OjMee Technology to submit an applicable
              privacy request using the contact information provided at
              the end of this Privacy Policy.
            </p>

            <p>
              We may request reasonable information to verify the identity
              or authority of the person submitting a privacy-related
              request before acting on it.
            </p>

            <p>
              Certain information may need to be retained even after a
              deletion or account-closure request where retention is
              required or reasonably necessary for legal, accounting,
              taxation, security, fraud-prevention or dispute-resolution
              purposes.
            </p>
          </LegalSection>

          <LegalSection
            number="11"
            title="Cookies, Analytics and Similar Technologies"
          >
            <p>
              OjMee Technology websites and applications may use cookies,
              local storage or similar technologies for authentication,
              login sessions, security, user preferences and essential
              application functionality.
            </p>

            <p>
              We may also use analytics technologies to understand website
              traffic, application usage, technical performance and how
              users interact with our digital services.
            </p>

            <p>
              Analytics information may help us identify technical issues,
              improve user experience and understand the performance of
              our websites and software products.
            </p>

            <p>
              Where consent or additional choices are required for
              non-essential technologies, appropriate information and
              controls may be provided in accordance with applicable
              requirements.
            </p>
          </LegalSection>

          <LegalSection
            number="12"
            title="Communications and Support"
          >
            <p>
              When users contact OjMee Technology for product information,
              technical assistance, demonstrations, account support or
              other inquiries, we may process the information provided
              in those communications.
            </p>

            <p>
              This information may be used to respond to requests,
              troubleshoot problems, maintain support records and improve
              our products and customer-support processes.
            </p>

            <p>
              We may also send service-related communications where
              necessary for account operation, security, subscriptions,
              important product updates or changes affecting a service
              used by the customer.
            </p>
          </LegalSection>

          <LegalSection
            number="13"
            title="Children's Privacy"
          >
            <p>
              OjMee Technology's SaaS products and business software are
              primarily designed for businesses, professionals and
              organizations and are not intended primarily as services
              directed to children.
            </p>

            <p>
              Where information relating to a child is processed,
              applicable legal requirements will be followed.
            </p>
          </LegalSection>

          <LegalSection
            number="14"
            title="Third-Party Links, Integrations and Services"
          >
            <p>
              OjMee Technology products may contain links to or
              integrations with third-party websites, APIs, payment
              providers, cloud services, communication platforms,
              authentication providers or other independent services.
            </p>

            <p>
              These third parties may independently collect or process
              information according to their own terms, privacy policies
              and security practices.
            </p>

            <p>
              OjMee Technology is not responsible for the privacy
              practices of independent third parties, and users should
              review the applicable policies of those services before
              providing information to them.
            </p>
          </LegalSection>


          <LegalSection
            number="15"
            title="Changes to this Privacy Policy"
          >
            <p>
              We may update this Privacy Policy periodically to reflect
              changes to our SaaS products, technology, security
              practices, business operations, service providers or
              applicable requirements.
            </p>

            <p>
              When this Privacy Policy is updated, the latest version
              will display a revised "Last Updated" date.
            </p>

            <p>
              Where appropriate, significant changes may also be
              communicated through our website, software application,
              account interface, email or another suitable communication
              channel.
            </p>

            <p>
              Users are encouraged to review this Privacy Policy
              periodically to remain informed about our information
              practices.
            </p>
          </LegalSection>

          <motion.article
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative w-full overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] p-6 sm:p-8 lg:p-10"
          >
            <div className="pointer-events-none absolute bottom-0 right-0 h-52 w-52 rounded-full bg-violet-500/[0.04] blur-3xl" />

            <div className="relative">

              <div className="flex items-start gap-4 sm:gap-5 lg:gap-6">

                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-violet-400/20 bg-violet-500/10 font-black text-violet-300 sm:h-14 sm:w-14">
                  16
                </div>

                <div>

                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-violet-300">
                    Privacy & Support
                  </p>

                  <h2 className="mt-2 text-xl font-bold text-white sm:text-2xl lg:text-[26px]">
                    Contact OjMee Technology
                  </h2>

                  <p className="mt-2 leading-7 text-slate-400">
                    Questions, concerns or requests relating to this
                    Privacy Policy, your account or personal information
                    may be submitted using the contact details below.
                  </p>

                </div>

              </div>


              <div className="mt-7 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">

                <InfoBox title="Company">
                  OjMee Technology
                </InfoBox>


                <InfoBox title="Website">
                  <a
                    href="https://ojmee.in"
                    target="_blank"
                    rel="noreferrer"
                    className="text-pink-300 transition hover:text-pink-200"
                  >
                    www.ojmee.in
                  </a>
                </InfoBox>


                <InfoBox title="Head Office">
                  C-58, Shahpuri Tower, Janakpuri, Delhi - 110058
                </InfoBox>


                <InfoBox title="Privacy / Support Email">
                  <a
                    href="mailto:ojmeetech@gmail.com"
                    className="text-pink-300 transition hover:text-pink-200"
                  >
                    ojmeetech@gmail.com
                  </a>
                </InfoBox>

              </div>

              <div className="mt-5 rounded-2xl border border-white/10 bg-black/10 p-5 sm:p-6">

                <p className="text-sm leading-7 text-slate-400 sm:text-base">
                  This Privacy Policy applies to OjMee Technology services
                  that reference this Policy. Individual SaaS products may
                  provide additional product-specific terms or privacy
                  information where required.
                </p>

              </div>

            </div>

          </motion.article>

        </div>

      </section>

    </main>
  );
}